import { useMemo, useState } from 'react'
import { useListScrapMaterials } from '@/shared/hooks/api/preconsumerScrapMaterials'
import AllocatedPreconsumerScrapTable from '../precursor-material-suppliers/components/AllocatedPreconsumerScrapTable'
import useI18n from '@/shared/hooks/useI18n'
import { LoadingScreen } from '@/shared/components'
import MissingResourcesAlert from '@/components/MissingResourcesAlert'
import ReportingPeriodsSelector, {
  getInitialReportingPeriod
} from '../../components/ReportingPeriodsSelector'
import { PreconsumerScrapMaterialWithEmissionReport } from '@/api/PreconsumerScrapMaterialModel'
import { DataBlock, PageSection } from '@/common'
import { UnitLabel } from '@/shared/config/config'

export default function PreconsumerScrapMaterialsBalance({
  installationId,
  reportingPeriods,
  reportingPeriodBasis
}: {
  installationId: string
  reportingPeriods: { startDate: string; endDate: string }[]
  reportingPeriodBasis: string
}) {
  const { t } = useI18n()

  const [selectedReportingPeriod, setSelectedReportingPeriod] = useState<{
    value: string | null
    startDate?: string
    endDate?: string
  }>(() => getInitialReportingPeriod(reportingPeriods))

  const { data: precursorMaterials, isPending: isPrecursorMaterialsPending } =
    useListScrapMaterials(installationId!)

  const allocatedMaterials = useMemo(() => {
    if (!precursorMaterials) {return []}

    const isScrapMaterialInReportingPeriod = (material: any): boolean => {
      if (!selectedReportingPeriod.startDate || !selectedReportingPeriod.endDate) {
        return true
      }

      const startDate = new Date(material.emissionReport.startDate)
      const endDate = new Date(material.emissionReport.endDate)

      return (
        startDate >= new Date(selectedReportingPeriod.startDate) &&
        endDate <= new Date(selectedReportingPeriod.endDate)
      )
    }

    return precursorMaterials.filter(material => {
      const isAllocated = material.stockChangeType === 'ALLOCATED'

      if (!isAllocated) {return false}

      const isInReportingPeriod = isScrapMaterialInReportingPeriod(material)
      return isInReportingPeriod
    })
  }, [precursorMaterials, selectedReportingPeriod])

  if (isPrecursorMaterialsPending) {
    return <LoadingScreen />
  }

  const hasAllocatedMaterials = allocatedMaterials && allocatedMaterials.length > 0

  const preconsumerScrapMaterialsGroupedByScrapType = allocatedMaterials
    .sort((prev, next) => {
      const datePrev = new Date(prev.emissionReport?.startDate ?? '').getTime()
      const dateNext = new Date(next.emissionReport?.startDate ?? '').getTime()
      if (datePrev !== dateNext) {return datePrev - dateNext}

      const routePrev = prev.emissionReport?.productionRoute?.name ?? ''
      const routeNext = next.emissionReport?.productionRoute?.name ?? ''
      return routePrev.localeCompare(routeNext)
    })
    .reduce(
      (acc, material) => {
        const key = material.details ?? ''
        acc[key] = [...(acc[key] || []), material]
        return acc
      },
      {} as Record<string, PreconsumerScrapMaterialWithEmissionReport[]>
    )

  return (
    <>
      <PageSection
        title={t('materials:balancePage.sections.allocated.title')}
        actions={
          <ReportingPeriodsSelector
            reportingPeriods={reportingPeriods}
            reportingPeriodBasis={reportingPeriodBasis}
            selectedReportingPeriod={selectedReportingPeriod}
            onChange={(value, startDate, endDate) => {
              setSelectedReportingPeriod({ value, startDate, endDate })
            }}
          />
        }
      >
        {hasAllocatedMaterials ? (
          <>
            <div className="md:w-1/6 sm:w-2/6 w-1/2">
              <DataBlock
                label={t('materials:balancePage.sections.allocated.totalAllocatedQuantity')}
                data={`${allocatedMaterials.reduce((acc, material) => acc + material.quantityInTonnes, 0)} ${UnitLabel.t}`}
              />
            </div>
            {Object.entries(preconsumerScrapMaterialsGroupedByScrapType).map(
              ([scrapType, materials]) =>
                materials.length > 0 && (
                  <div key={scrapType}>
                    <h4 className="font-semibold mb-2">{scrapType}</h4>
                    <AllocatedPreconsumerScrapTable materials={materials} />
                  </div>
                )
            )}
          </>
        ) : (
          <MissingResourcesAlert
            title={t('materials:allocatedStockTable.noData.title')}
            description={t('materials:allocatedStockTable.noData.description')}
          />
        )}
      </PageSection>
    </>
  )
}
